// Core HTTP client (migrated from previous src/api/apiClient.ts)
import { getVersionedEndpoint, apiClientsRegistry } from './apiConfig';
import type { LC_ErrorOut, ApiResponse } from '../shared';


export class ApiError extends Error {
  constructor(
    public status: number,
    public error: LC_ErrorOut,
    message?: string,
  ) {
    super(message || error.description);
    this.name = 'ApiError';
  }
}

const UNAUTHORIZED_COOLDOWN_MS = 500;

type ClientRequestOptions = {
  /** default true; when false, do not inject Authorization header */
  authRequired?: boolean;
  /** default false; when true, do not call global 401/403 handler */
  skipUnauthorizedHandler?: boolean;
  /** default 'omit'; use 'include' for SSO endpoints that require browser fingerprinting */
  credentials?: RequestCredentials;
  /** Optional signal to cancel the request (e.g. on unmount or deps change) */
  signal?: AbortSignal;
  /** request body, propagated to fetch (e.g. JSON.stringify(params) for POST downloads) */
  body?: BodyInit | null;
};

class ApiClient {
  private baseUrl: string;
  private timeout: number;
  private tokenProvider: (() => string | null) | null = null;
  private unauthorizedHandler: ((errorCode?: string) => void) | null = null;
  private forbiddenHandler: (() => void) | null = null;
  private inflightGet: Map<string, {
    promise: Promise<ApiResponse<unknown>>;
    signal?: AbortSignal;
  }> = new Map();
  private lastUnauthorizedAt = 0;
  private lastForbiddenAt = 0;

  constructor(baseUrl: string, timeout: number) {
    this.baseUrl = baseUrl.replace(/\/$/, '');
    this.timeout = timeout;
  }

  setTokenProvider(provider: () => string | null) {
    this.tokenProvider = provider;
  }

  setUnauthorizedHandler(handler: (errorCode?: string) => void) {
    this.unauthorizedHandler = handler;
  }

  setForbiddenHandler(handler: () => void) {
    this.forbiddenHandler = handler;
  }

  private resolveToken(): string | null {
    return this.tokenProvider ? this.tokenProvider() : null;
  }

  private buildAbortSignal(userSignal?: AbortSignal, timeoutMs?: number): AbortSignal {
    const effectiveTimeout = timeoutMs ?? this.timeout;
    if (!userSignal) return AbortSignal.timeout(effectiveTimeout);
    return AbortSignal.any([userSignal, AbortSignal.timeout(effectiveTimeout)]);
  }

  private getHeaders(includeAuth: boolean = true): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
    if (includeAuth) {
      const token = this.resolveToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  private async request<T>(
    endpoint: string,
    options: (RequestInit & ClientRequestOptions) = {},
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${getVersionedEndpoint(endpoint)}`;
    const method = (options.method || 'GET').toString().toUpperCase();
    const { authRequired = true, skipUnauthorizedHandler = false } = options;
    const cacheKeyObj = {
      method,
      url,
      authRequired,
      skipUnauthorizedHandler,
      credentials: options.credentials ?? 'omit',
    };
    const cacheKey = JSON.stringify(cacheKeyObj);

    if (method === 'GET' && this.inflightGet.has(cacheKey)) {
      const cached = this.inflightGet.get(cacheKey)!;
      if (!cached.signal?.aborted) {
        return cached.promise as Promise<ApiResponse<T>>;
      }
      this.inflightGet.delete(cacheKey);
    }

    const exec = this.executeRequest<T>(
      url,
      endpoint,
      options,
      authRequired,
      skipUnauthorizedHandler,
    );

    if (method === 'GET') {
      this.inflightGet.set(cacheKey, {
        promise: exec as unknown as Promise<ApiResponse<unknown>>,
        signal: options.signal,
      });
      try {
        return await exec;
      } finally {
        this.inflightGet.delete(cacheKey);
      }
    }

    return exec;
  }

  private async executeRequest<T>(
    url: string,
    endpoint: string,
    options: (RequestInit & ClientRequestOptions),
    authRequired: boolean,
    skipUnauthorizedHandler: boolean,
  ): Promise<ApiResponse<T>> {
    try {
      const {
        authRequired: _ar,
        skipUnauthorizedHandler: _suh,
        credentials: _creds,
        signal: userSignal,
        ...reqInit
      } = options;
      const response = await fetch(url, {
        ...reqInit,
        headers: { ...this.getHeaders(authRequired), ...reqInit.headers },
        mode: 'cors',
        credentials: options.credentials ?? 'omit',
        signal: this.buildAbortSignal(userSignal),
      });
      // Handle 204 No Content as success
      if (response.status === 204) {
        return {
          data: null as T,
          success: true,
          status: 204,
        };
      }

      const contentType = response.headers.get('content-type') || '';
      const isJson = contentType.includes('application/json');
      
      if (!isJson) {
        // Non-JSON responses (e.g., text/plain) - treat as error
        const text = await response.text();
        const errOut: LC_ErrorOut = {
          description: text || `HTTP ${response.status}: ${response.statusText}`,
        };
        this.handleUnauthorized(response, skipUnauthorizedHandler, undefined);
        throw new ApiError(response.status, errOut);
      }

      const envelope = await response.json();
      return this.unwrapEnvelope<T>(envelope, response, skipUnauthorizedHandler);
    } catch (error) {
      return this.handleRequestError(error, endpoint);
    }
  }

  /**
   * Unwrap API envelope and handle pagination if present
   */
  private unwrapEnvelope<T>(
    envelope: any,
    response: Response,
    skipUnauthorizedHandler: boolean,
  ): ApiResponse<T> {
    // Handle error cases: non-ok HTTP status, or envelope explicitly signals failure
    if (!response.ok || envelope.success === false) {
      // Support both backend error formats:
      // 1. Wrapped: { success: false, status, error: { description, code, detail, ... } }
      // 2. Direct:  { description, code, detail, validationErrors, ... }
      const errOut: LC_ErrorOut = envelope.error
        ? envelope.error
        : {
          description: envelope.description,
          detail: envelope.detail,
          code: envelope.code,
          validationErrors: envelope.validationErrors,
        };
      this.handleUnauthorized(response, skipUnauthorizedHandler, errOut.code ?? undefined);
      throw new ApiError(response.status, errOut);
    }

    // Non-envelope response: the body IS the data (e.g. endpoints that return the DTO directly)
    if (!('success' in envelope)) {
      return {
        data: envelope as T,
        success: true,
        status: response.status,
      };
    }

    // Handle success envelope with optional pagination
    // If pagination exists at root level, restructure as LC_PagedResult.
    // Any sibling fields beyond the standard envelope keys (e.g. validatedSelection)
    // are preserved alongside data + pagination so callers can read them.
    if ('pagination' in envelope && envelope.pagination) {
      const { success: _success, status: _status, error: _error, data, pagination, ...siblings } =
        envelope as Record<string, unknown>;
      return {
        data: {
          ...siblings,
          data,
          pagination,
        } as T,
        success: true,
        status: response.status,
      };
    }

    // Simple success envelope without pagination
    return {
      data: envelope.data as T,
      success: true,
      status: response.status,
    };
  }

  private normalizeApiError(
    response: Response,
    responseData: any,
    isJson: boolean,
  ): LC_ErrorOut {
    // For envelope format, error is inside responseData.error
    if (isJson && responseData && typeof responseData === 'object' && 'error' in responseData) {
      const envelopeError = responseData.error as LC_ErrorOut;
      // Ensure description exists, generate generic message if missing
      if (!envelopeError.description) {
        envelopeError.description = `HTTP ${response.status}: ${response.statusText}`;
      }
      return envelopeError;
    }
    
    // Fallback for non-envelope errors
    const text = typeof responseData === 'string' ? responseData : '';
    return {
      description: text || `HTTP ${response.status}: ${response.statusText}`,
    } as LC_ErrorOut;
  }

  private handleUnauthorized(
    response: Response,
    skipUnauthorizedHandler: boolean,
    errorCode?: string,
  ) {
    if (skipUnauthorizedHandler) return;
    const now = Date.now();
    if (response.status === 401 && this.unauthorizedHandler) {
      if (now - this.lastUnauthorizedAt >= UNAUTHORIZED_COOLDOWN_MS) {
        this.lastUnauthorizedAt = now;
        this.unauthorizedHandler(errorCode);
      }
    } else if (response.status === 403 && this.forbiddenHandler) {
      if (now - this.lastForbiddenAt >= UNAUTHORIZED_COOLDOWN_MS) {
        this.lastForbiddenAt = now;
        this.forbiddenHandler();
      }
    }
  }

  private handleRequestError(
    error: unknown,
    endpoint: string,
  ): ApiResponse<never> {
    if (error instanceof ApiError) {
      return { data: null, success: false, error: error.error, status: error.status };
    }
    if (error instanceof DOMException) {
      if (error.name === 'TimeoutError' || error.name === 'AbortError') {
        const isTimeout = error.name === 'TimeoutError';
        return {
          data: null,
          success: false,
          error: isTimeout
            ? {
              description: 'Tiempo de espera agotado',
              detail: `Se agotó el tiempo de espera al llamar a ${endpoint}`,
              code: 'TIMEOUT',
            }
            : {
              description: 'Solicitud cancelada',
              detail: `La solicitud a ${endpoint} fue cancelada`,
              code: 'ABORTED',
            },
        };
      }
    }
    if (error instanceof TypeError || error instanceof Error) {
      return {
        data: null,
        success: false,
        error: {
          description: 'Error de red',
          detail: `Ocurrió un error de red al llamar a ${endpoint}`,
          code: 'NETWORK_ERROR',
        },
      };
    }
    return {
      data: null,
      success: false,
      error: {
        description: 'Error desconocido',
        detail: `Ocurrió un error desconocido al llamar a ${endpoint}`,
        code: 'UNKNOWN_ERROR',
      },
    };
  }

  async get<T>(endpoint: string, options?: ClientRequestOptions) {
    return this.request<T>(endpoint, { method: 'GET', ...(options || {}) });
  }
  async post<T>(endpoint: string, data?: unknown, options?: ClientRequestOptions) {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      ...(options || {}),
    });
  }
  async put<T>(endpoint: string, data?: unknown, options?: ClientRequestOptions) {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      ...(options || {}),
    });
  }
  async patch<T>(endpoint: string, data?: unknown, options?: ClientRequestOptions) {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
      ...(options || {}),
    });
  }
  async delete<T>(endpoint: string, data?: unknown, options?: ClientRequestOptions) {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      body: data ? JSON.stringify(data) : undefined,
      ...(options || {}),
    });
  }

  /**
   * Generic method for downloading Blob responses (files, exports, etc.)
   * @param endpoint - API endpoint
   * @param data - Request payload
   * @param options - Additional request options
   * @returns ApiResponse with Blob data
   */
  async downloadBlob(
    endpoint: string,
    data?: unknown,
    options?: ClientRequestOptions & { method?: 'GET' | 'POST' },
  ): Promise<ApiResponse<Blob>> {
    const url = `${this.baseUrl}${getVersionedEndpoint(endpoint)}`;
    const method = options?.method || 'POST';
    const { authRequired = true, skipUnauthorizedHandler = false } = options || {};

    try {
      const {
        authRequired: _ar,
        skipUnauthorizedHandler: _suh,
        method: _m,
        signal: userSignal,
        ...reqInit
      } = options || {};
      const response = await fetch(url, {
        method,
        headers: this.getHeaders(authRequired),
        body: data ? JSON.stringify(data) : undefined,
        mode: 'cors',
        credentials: 'omit',
        ...reqInit,
        signal: this.buildAbortSignal(userSignal),
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type') || '';
        const isJson = contentType.includes('application/json');
        const responseData = isJson ? await response.json() : await response.text();
        const errOut = this.normalizeApiError(response, responseData, isJson);
        this.handleUnauthorized(response, skipUnauthorizedHandler);
        throw new ApiError(response.status, errOut);
      }

      const blob = await response.blob();
      return { data: blob, success: true };
    } catch (error) {
      return this.handleRequestError(error, endpoint);
    }
  }

  /**
   * Downloads a Blob and extracts the filename from the Content-Disposition response header.
   * @param endpoint - API endpoint
   * @param options - Additional request options
   * @returns ApiResponse with Blob data and filename string
   */
  async downloadBlobWithFilename(
    endpoint: string,
    options?: ClientRequestOptions & { method?: 'GET' | 'POST'; timeoutMs?: number },
  ): Promise<ApiResponse<{ blob: Blob; filename: string }>> {
    const url = `${this.baseUrl}${getVersionedEndpoint(endpoint)}`;
    const method = options?.method || 'GET';
    const { authRequired = true, skipUnauthorizedHandler = false } = options || {};

    try {
      const {
        authRequired: _ar,
        skipUnauthorizedHandler: _suh,
        method: _m,
        timeoutMs,
        signal: userSignal,
        ...reqInit
      } = options || {};
      const response = await fetch(url, {
        method,
        headers: this.getHeaders(authRequired),
        mode: 'cors',
        credentials: 'omit',
        ...reqInit,
        signal: this.buildAbortSignal(userSignal, timeoutMs),
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type') || '';
        const isJson = contentType.includes('application/json');
        const responseData = isJson ? await response.json() : await response.text();
        const errOut = this.normalizeApiError(response, responseData, isJson);
        this.handleUnauthorized(response, skipUnauthorizedHandler);
        throw new ApiError(response.status, errOut);
      }

      const disposition = response.headers.get('content-disposition') || '';
      const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^;"\n]+)"?/i);
      const filename = match ? decodeURIComponent(match[1].trim()) : 'export.xlsx';

      const blob = await response.blob();
      return { data: { blob, filename }, success: true };
    } catch (error) {
      return this.handleRequestError(error, endpoint);
    }
  }

  /**
   * Handles hybrid endpoints that return either:
   * - 200 with a binary file (synchronous export ready)
   * - 202 with a JSON job reference (asynchronous export queued)
   */
  async downloadBlobOrAcceptJob(
    endpoint: string,
    options?: ClientRequestOptions & { method?: 'GET' | 'POST' },
  ): Promise<ApiResponse<{ type: 'sync'; blob: Blob; filename: string } | { type: 'async'; jobId: string }>> {
    const url = `${this.baseUrl}${getVersionedEndpoint(endpoint)}`;
    const method = options?.method || 'GET';
    const { authRequired = true, skipUnauthorizedHandler = false } = options || {};

    try {
      const {
        authRequired: _ar,
        skipUnauthorizedHandler: _suh,
        method: _m,
        signal: userSignal,
        ...reqInit
      } = options || {};
      const response = await fetch(url, {
        method,
        headers: this.getHeaders(authRequired),
        mode: 'cors',
        credentials: 'omit',
        ...reqInit,
        signal: this.buildAbortSignal(userSignal),
      });

      if (response.status === 200) {
        const disposition = response.headers.get('content-disposition') || '';
        const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^;"\n]+)"?/i);
        const filename = match ? decodeURIComponent(match[1].trim()) : 'export.xlsx';
        const blob = await response.blob();
        return { data: { type: 'sync', blob, filename }, success: true };
      }

      if (response.status === 202) {
        const body = await response.json();
        const jobId: string | undefined = body?.data?.jobId ?? body?.jobId;
        if (typeof jobId !== 'string' || jobId.trim() === '') {
          const errOut = this.normalizeApiError(response, body, true);
          throw new ApiError(202, errOut);
        }
        return { data: { type: 'async', jobId }, success: true };
      }

      const contentType = response.headers.get('content-type') || '';
      const isJson = contentType.includes('application/json');
      const responseData = isJson ? await response.json() : await response.text();
      const errOut = this.normalizeApiError(response, responseData, isJson);
      this.handleUnauthorized(response, skipUnauthorizedHandler);
      throw new ApiError(response.status, errOut);
    } catch (error) {
      return this.handleRequestError(error, endpoint);
    }
  }
}

export const createApiClient = (name: string = 'core'): ApiClient => {
  const cfg = apiClientsRegistry[name] || apiClientsRegistry.core;
  return new ApiClient(cfg.baseUrl, cfg.timeout);
};

export const apiClient = createApiClient('core');